/**
 * Audit log (admins): who did what, when, to which document - filtered by
 * company, kind of event, period and a search box, with CSV export. Platform
 * admins also see every email DocVault sent and can run the daily reminders.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, type AuditRow, type NotificationRow } from "../lib/api";
import { useStore } from "../lib/store";
import { coName } from "../lib/brand";
import { fmtDT, saveBlob } from "../lib/format";
import { Co, Empty, Loading, TypeBadge } from "../components/bits";
import { Icon } from "../components/Icon";
import { LOCAL_AUTH } from "../auth/cognito";
import { PartTag } from "./settings/SettingsLayout";

const KINDS: [string, string][] = [
  ["", "Everything"], ["approvals", "Approvals"], ["documents", "Uploads"], ["access", "Views & downloads"], ["signins", "Sign-ins"], ["admin", "Admin changes"],
  ["workflows", "Workflows"], ["clienthub", "TechHub"], ["reviewhub", "ReviewHub"], ["vendorhub", "VendorHub"],
];
const VERB: Record<string, string> = {
  "document.create": "Created", "document.tags": "Changed the framework tags on", "version.create": "Uploaded a new file for", "version.upload_confirmed": "Finished uploading",
  "version.submit": "Submitted", "version.withdraw": "Withdrew", "version.approve": "Approved", "version.changes_requested": "Requested changes on",
  "version.render_requested": "Asked for a new PDF copy of", "document.view": "Viewed", "document.download": "Downloaded",
  "user.login": "Signed in", "user.signout_all": "Signed out everywhere", "publish_key.create": "Created a publishing key", "publish_key.revoke": "Revoked a publishing key", "profile.update": "Updated their profile", "membership.grant": "Changed roles", "membership.revoke": "Removed a member",
  "tenant.policy": "Changed the approval policy", "tenant.settings": "Changed company settings",
  "document.archive": "Archived", "document.restore": "Restored", "document.checkout": "Checked out", "document.checkin": "Checked in",
  "document.retire": "Retired", "document.renumber": "Renumbered", "document.meta": "Changed the classification or details of", "document.readers": "Changed who can read",
  "version.published": "Published (effective date reached)", "code.add": "Added a code", "code.update": "Changed a code", "code.remove": "Deleted a code", "search_term.add": "Added an acronym", "search_term.remove": "Removed an acronym", "audit.export": "Exported the audit log",
  // Every other action in backend/src/lib/audit.ts, so none shows as a raw code.
  "user.mfa_on": "Turned on two-step sign-in", "user.mfa_off": "Turned off two-step sign-in", "user.mfa_reset": "Reset someone's two-step sign-in",
  "document.note": "Added a note to", "document.hold": "Put on hold or took off hold:", "document.acknowledge": "Acknowledged", "document.ack_setting": "Changed who must acknowledge",
  "document.suggestion": "Suggested a change to", "document.suggestion_accept": "Accepted a suggestion on", "document.suggestion_decline": "Declined a suggestion on",
  "document.reviewed": "Marked reviewed, no changes:", "editor.open": "Opened in the editor", "editor.save": "Saved in the editor",
  "version.reviewed": "Reviewed", "version.approve_partial": "Gave one of the approvals for",
  "approval_rule.set": "Set an approval rule", "approval_rule.remove": "Removed an approval rule", "required_reading.update": "Changed required reading", "onboarding.update": "Changed the onboarding list",
  "module.switch": "Turned an add-on on or off", "person.update": "Changed a person's manager or dates", "ai.settings": "Changed the AI settings", "ai.use": "Used the AI assistant",
  "integration.connect": "Connected an integration", "integration.update": "Changed an integration", "integration.remove": "Removed an integration", "integration.sync": "Synced an integration",
  "integration.map": "Matched integration records", "integration.guide": "Changed an integration guide",
  "workflow.start": "Started a workflow", "workflow.step": "Updated a workflow step", "workflow.finish": "Finished a workflow", "workflow.cancel": "Canceled a workflow",
  "checklist_template.update": "Changed a workflow template", "checklist.start": "Started a client checklist", "checklist.step": "Updated a client checklist step",
  "checklist.finish": "Finished a client checklist", "checklist.cancel": "Canceled a client checklist",
  "clienthub.client.create": "TechHub: added a client", "clienthub.client.update": "TechHub: changed a client", "clienthub.client.verify": "TechHub: confirmed a client's details",
  "clienthub.site.add": "TechHub: added a site", "clienthub.site.update": "TechHub: changed a site", "clienthub.site.remove": "TechHub: removed a site",
  "clienthub.contact.add": "TechHub: added a contact", "clienthub.contact.update": "TechHub: changed a contact", "clienthub.contact.remove": "TechHub: removed a contact",
  "clienthub.user.add": "TechHub: added a client user", "clienthub.user.update": "TechHub: changed a client user", "clienthub.user.remove": "TechHub: removed a client user",
  "clienthub.m365.connect": "TechHub: started a Microsoft 365 connection", "clienthub.m365.approved": "TechHub: connected Microsoft 365", "clienthub.m365.remove": "TechHub: disconnected Microsoft 365",
  "clienthub.m365.app_save": "TechHub: saved the Microsoft 365 app details", "clienthub.m365.app_remove": "TechHub: removed the Microsoft 365 app details",
  "clienthub.note.add": "TechHub: added a note", "clienthub.service.add": "TechHub: added a service", "clienthub.service.update": "TechHub: changed a service",
  "clienthub.doc.link": "TechHub: linked a document", "clienthub.doc.update": "TechHub: changed a linked document", "clienthub.doc.unlink": "TechHub: unlinked a document",
  "clienthub.network.add": "TechHub: added a network", "clienthub.network.update": "TechHub: changed a network", "clienthub.network.remove": "TechHub: removed a network",
  "clienthub.network.dismiss": "TechHub: dismissed a network suggestion",
  "clienthub.ip.add": "TechHub: added an IP address", "clienthub.ip.update": "TechHub: changed an IP address", "clienthub.ip.remove": "TechHub: removed an IP address",
  "clienthub.internet.add": "TechHub: added an internet connection", "clienthub.internet.update": "TechHub: changed an internet connection", "clienthub.internet.remove": "TechHub: removed an internet connection",
  "clienthub.asset.add": "TechHub: added an asset", "clienthub.asset.update": "TechHub: changed an asset", "clienthub.asset.remove": "TechHub: removed an asset", "clienthub.asset.import": "TechHub: imported assets",
  "clienthub.agreement.add": "TechHub: added an agreement", "clienthub.agreement.update": "TechHub: changed an agreement", "clienthub.agreement.remove": "TechHub: removed an agreement",
  "clienthub.agreement.file_add": "TechHub: added an agreement file", "clienthub.agreement.file_update": "TechHub: changed an agreement file",
  "clienthub.agreement.file_remove": "TechHub: removed an agreement file", "clienthub.agreement.file_view": "TechHub: opened an agreement file",
  "clienthub.agreement.reminder_add": "TechHub: added an agreement reminder", "clienthub.agreement.reminder_done": "TechHub: finished an agreement reminder",
  "clienthub.agreement_type.add": "TechHub: added an agreement type", "clienthub.agreement_type.update": "TechHub: changed an agreement type",
  "clienthub.domain.add": "TechHub: added a domain", "clienthub.domain.update": "TechHub: changed a domain", "clienthub.domain.remove": "TechHub: removed a domain",
  "reviewhub.review.create": "ReviewHub: started a review", "reviewhub.review.update": "ReviewHub: changed a review", "reviewhub.review.stage": "ReviewHub: moved a review to the next stage",
  "reviewhub.metrics": "ReviewHub: changed the metrics", "reviewhub.risk": "ReviewHub: changed a risk", "reviewhub.roadmap": "ReviewHub: changed the roadmap",
  "reviewhub.commitment": "ReviewHub: changed a commitment", "reviewhub.section": "ReviewHub: changed a section", "reviewhub.pitch": "ReviewHub: asked for a Pitch check",
  "reviewhub.page.delivered": "ReviewHub: gave the client the review page", "reviewhub.pulse.add": "ReviewHub: added a pulse check", "reviewhub.evidence.pull": "ReviewHub: pulled in evidence",
  "reviewhub.upload.add": "ReviewHub: added a file", "reviewhub.upload.remove": "ReviewHub: removed a file", "reviewhub.settings": "ReviewHub: changed the settings",
  "vendorhub.vendor.create": "VendorHub: added a vendor", "vendorhub.vendor.update": "VendorHub: changed a vendor", "vendorhub.vendor.status": "VendorHub: changed a vendor's status",
  "vendorhub.vendor.review": "VendorHub: reviewed a vendor", "vendorhub.evidence.add": "VendorHub: added evidence", "vendorhub.evidence.open": "VendorHub: opened evidence",
  "vendorhub.evidence.remove": "VendorHub: removed evidence", "vendorhub.finding": "VendorHub: added or changed a finding", "vendorhub.settings": "VendorHub: changed the settings",
};
const KIND_LABEL: Record<string, string> = {
  approval_request: "Approval request", changes_requested: "Changes requested", approved: "Approved", published: "Published",
  review_reminder: "Review reminder", digest: "Weekly digest", invite: "Invitation",
};

/** Color of the marker beside each event (status colors, always with the verb). */
function actKind(a: string): string {
  if (a === "version.approve") return "ok";
  if (a === "version.changes_requested" || a === "membership.revoke") return "bad";
  if (a === "version.submit" || a === "version.withdraw") return "warn";
  if (a.startsWith("document.view") || a.startsWith("document.download") || a === "user.login") return "mute";
  if (a.startsWith("membership.") || a.startsWith("tenant.") || a.startsWith("code.") || a.startsWith("search_term") || a.startsWith("audit.") || a === "profile.update") return "adm";
  return "info";
}

function detail(r: AuditRow): string {
  const d = r.details ?? {};
  if (r.action === "version.approve") return [typeof d.number === "string" ? d.number : "", d.self_approved === true ? "self-approved" : "",
    typeof d.effective_on === "string" ? `effective ${d.effective_on}` : ""].filter(Boolean).join(" · ");
  if (r.action === "version.changes_requested" && typeof d.comment === "string") return `“${d.comment}”`;
  if (r.action === "version.submit" && d.resubmitted === true) return "resubmitted";
  if ((r.action === "document.view" || r.action === "document.download") && typeof d.kind === "string") return d.kind === "pdf" ? "PDF copy" : "original file";
  if (r.action === "membership.grant" && Array.isArray(d.after)) return `now: ${(d.after as string[]).join(", ")}`;
  if (r.action === "document.tags" && Array.isArray(d.after)) return (d.after as string[]).length ? `now: ${(d.after as string[]).join(", ")}` : "all tags removed";
  if (r.action === "tenant.policy") return d.require_second_approver ? "second approver required" : "self-approval allowed";
  if (r.action.startsWith("search_term") && typeof d.term === "string") return d.term;
  if (r.action === "document.renumber") return `${d.before} → ${d.after}`;
  if (r.action.startsWith("code.")) return [d.kind, d.code, typeof d.label === "string" ? d.label : ""].filter(Boolean).join(" · ");
  if ((r.action === "document.retire" || r.action === "document.archive") && typeof d.reason === "string" && d.reason) return `“${d.reason}”`;
  if (r.action.startsWith("workflow.") || r.action.startsWith("checklist")) return [d.workflow ?? d.checklist ?? d.template, d.step].filter((x) => typeof x === "string" && x).join(" · ");
  return "";
}

export function AuditPage() {
  const { me, p, toast } = useStore();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "emails" && me.user.is_platform_admin ? "emails" : "activity";
  if (!p.anyAdmin) return <Navigate to="/" replace />;
  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <PartTag part="account" />
          <h1 className="title">Audit <em>log</em></h1>
          <div className="lede">Every submission, approval, view, download and admin change in the companies you administer. Nothing here can be edited or deleted.</div>
        </div>
      </div>
      {me.user.is_platform_admin && (
        <div className="tabs" role="tablist">
          <button className={`tab ${tab === "activity" ? "on" : ""}`} role="tab" onClick={() => setSp({}, { replace: true })}>Activity</button>
          <button className={`tab ${tab === "emails" ? "on" : ""}`} role="tab" onClick={() => setSp({ tab: "emails" }, { replace: true })}>Emails sent</button>
        </div>
      )}
      {tab === "activity" ? <Activity /> : <Emails toast={toast} />}
    </div>
  );
}

function Activity() {
  const { p, toast } = useStore();
  const [days, setDays] = useState(30);
  const [company, setCompany] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const companies = ["CPY", ...p.adminCodes];

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      setErr(null);
      api.audit({ days, company, kind, q: q.trim() }).then(
        (r) => { if (live) { setRows(r.rows); setTotal(r.total); } },
        (e: Error) => live && setErr(e.message));
    }, q ? 250 : 0);
    return () => { live = false; clearTimeout(t); };
  }, [days, company, kind, q]);

  const more = async () => {
    if (!rows) return;
    try { const r = await api.audit({ days, company, kind, q: q.trim(), offset: rows.length }); setRows([...rows, ...r.rows]); }
    catch (e) { toast((e as Error).message); }
  };
  const exportCsv = async () => {
    setBusy(true);
    try { saveBlob(await api.auditCsv(days, { company, kind, q: q.trim() }), `docvault-audit-${days}d-${new Date().toISOString().slice(0, 10)}.csv`); toast("Audit log exported."); }
    catch (e) { toast((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="filters">
        <label className="inline-search"><Icon name="search" size={16} />
          <input placeholder="Person, document title or number" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the audit log" />
        </label>
        <select className={`fsel ${company ? "set" : ""}`} value={company} onChange={(e) => setCompany(e.target.value)} aria-label="Company">
          <option value="">All companies</option>
          {companies.map((c) => <option key={c} value={c}>{coName(c)}</option>)}
        </select>
        <select className="fsel" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        <span className="spacer" />
        <button className={`btn btn-ghost btn-sm ${busy ? "busy" : ""}`} onClick={() => void exportCsv()}><Icon name="dl" />Export CSV</button>
      </div>
      <div className="chips" style={{ marginBottom: 12 }}>
        {KINDS.map(([k, l]) => <button key={k} className={`chip ${kind === k ? "on" : ""}`} onClick={() => setKind(k)}>{l}</button>)}
      </div>
      {err && <div className="note bad">{err}</div>}
      <div className="card tbl-wrap">
        {!rows ? <Loading /> : rows.length ? (
          <table className="tbl dt">
            <colgroup><col style={{ width: 180 }} /><col style={{ width: 190 }} /><col /><col className="hide-md" style={{ width: 220 }} /></colgroup>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th className="hide-md">Company</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ cursor: "default" }}>
                  <td className="due">{fmtDT(r.at)}</td>
                  <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.email ?? ""}>{r.actor ?? r.email ?? "-"}</td>
                  <td>
                    <i className={`act-dot ${actKind(r.action)}`} aria-hidden="true" />
                    <span style={{ fontWeight: 600, color: "var(--ink)" }}>{VERB[r.action] ?? r.action}</span>{" "}
                    {r.document_id && r.title ? <Link to={`/doc/${r.document_id}`} className="audit-doc">{r.number && <TypeBadge type={r.number.split("-")[1] ?? ""} sm />}<span className="docno">{r.number}</span> {r.title}</Link> : null}
                    {detail(r) && <div className="help" style={{ margin: "2px 0 0" }}>{detail(r)}</div>}
                  </td>
                  <td className="hide-md">{r.company ? <Co code={r.company} /> : <span style={{ color: "var(--faint)" }}>Account</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty title="Nothing here">No events match these filters.</Empty>}
      </div>
      {rows && (
        <div className="foot-note" style={{ display: "flex", alignItems: "center", gap: 12 }}>
          Showing {rows.length} of {total}.
          {rows.length < total && <button className="btn btn-ghost btn-sm" onClick={() => void more()}>Show more</button>}
        </div>
      )}
    </>
  );
}

function Emails({ toast }: { toast: (m: string) => void }) {
  const [rows, setRows] = useState<NotificationRow[] | null>(null);
  const [digest, setDigest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null); // last "Send now" result stays on screen
  const load = () => api.notifications(30).then((r) => setRows(r.rows), (e: Error) => toast(e.message));
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.runDailyJob(digest);
      const msg = `Sent ${r.reminders} review reminder${r.reminders === 1 ? "" : "s"}${digest ? ` and ${r.digests} digest${r.digests === 1 ? "" : "s"}` : ""}${r.failed ? `; ${r.failed} failed` : ""}${r.published ? `; published ${r.published} scheduled version${r.published === 1 ? "" : "s"}` : ""}${r.indexed ? `; queued ${r.indexed} file${r.indexed === 1 ? "" : "s"} for text search` : ""}.`;
      toast(msg);
      setResult(`${msg} (${new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})`);
      await load();
    } catch (e) { toast((e as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="card sect" style={{ marginBottom: 14, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h3>Review reminders</h3>
          <p className="cap" style={{ margin: 0 }}>They go out automatically every morning, and the weekly digest on Mondays. Run them now to test; nobody gets the same reminder twice in a day.</p>
          {result && <p className="cap" role="status" style={{ margin: "6px 0 0", color: "var(--ink)", fontWeight: 600 }}>Last run: {result}</p>}
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" checked={digest} onChange={(e) => setDigest(e.target.checked)} /> Include the weekly digest
        </label>
        <button className={`btn btn-accent btn-sm ${busy ? "busy" : ""}`} onClick={() => void run()}>Send now</button>
      </div>
      <div className="card tbl-wrap">
        {!rows ? <Loading /> : rows.length ? (
          <table className="tbl dt">
            <colgroup><col style={{ width: 180 }} /><col style={{ width: 230 }} /><col className="hide-md" style={{ width: 160 }} /><col /><col style={{ width: 100 }} /></colgroup>
            <thead><tr><th>When</th><th>To</th><th className="hide-md">Type</th><th>Subject</th><th>Status</th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ cursor: "default" }}>
                  <td className="due">{fmtDT(r.at)}</td>
                  <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.email}</td>
                  <td className="hide-md">{KIND_LABEL[r.kind] ?? r.kind}</td>
                  <td style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.subject}>{r.subject}</td>
                  <td>{r.status === "sent" ? <span className="pill sm pub">Sent</span> : <span className="pill sm chg" title={r.error ?? ""}>Failed</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <Empty title="No emails yet">Emails appear here once someone submits, approves or requests changes.</Empty>}
      </div>
      <div className="foot-note">Last 30 days, newest first.{LOCAL_AUTH && <> Locally, open the emails at <b>http://localhost:8025</b>.</>}</div>
    </>
  );
}
